import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/fireman-illustration_613284-294.jpg';

function EmergencyResponseRecovery() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (match structure used on other extended pages)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Activation & Operations',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>SEOC levels:</span>
              <span style={valText}>
                Level 3 monitoring (steady-state), Level 2 partial activation, Level 1 full activation. Daily situational reports and unified planning maintain a common operating picture.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Incident Action Planning:</span>
              <span style={valText}>
                SERT develops objectives, assigns missions to ESFs, tracks resources, and coordinates state/federal support as needed.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Roles & Responsibilities',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Governor:', 'Declares state of emergency; designates the State Coordinating Officer (SCO).'],
            ['SCO:', 'Manages and coordinates state response and recovery; can commit state resources.'],
            ['SERT:', 'Operates under ICS/ESF structure with Planning, Operations, Logistics, Finance/Admin, and Recovery sections.'],
          ].map(([k, v], i) => (
            <li key={`roles-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Emergency Declarations',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Local declarations:', 'Mayors/county boards declare local emergencies; can request state assistance.'],
            ['State declaration:', 'Governor issues executive order, enabling statewide coordination and emergency powers.'],
            ['Federal declarations:', 'Governor may request Emergency or Major Disaster declarations; FEMA coordinates federal aid with the state.'],
          ].map(([k, v], i) => (
            <li key={`decl-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Alerts, Warning & Communications',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>State Watch Office:</span>
              <span style={valText}>
                24/7 hub for monitoring hazards, issuing notifications, and coordinating alerts with counties and partners.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Interoperability:</span>
              <span style={valText}>
                Multiple networks (FIN, SLERS, EMNet, NOAA links, HF programs) ensure resilient communications during incidents.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Evacuation & Sheltering',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Evacuations:', 'Coordinated regionally; may lift tolls and lock drawbridges for clearance and traffic flow.'],
            ['Shelters:', 'General population and Special Needs Shelters must be physically/programmatically accessible (ADA compliant).'],
            ['Pets & service animals:', 'Sheltering plans accommodate service animals and address pet sheltering with agriculture partners.'],
          ].map(([k, v], i) => (
            <li key={`evac-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Resource Management & Mutual Aid',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Logistics:', 'Manages staging areas, base camps, points of distribution, and field support.'],
            ['Mutual Aid:', 'Uses Statewide Mutual Aid Agreement (SMAA) and EMAC to source resources from other jurisdictions.'],
            ['Air Operations:', 'Coordinates aviation assets and airspace to support missions statewide.'],
          ].map(([k, v], i) => (
            <li key={`rm-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Recovery Programs',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Public Assistance:', 'Cost-shared aid for debris removal, emergency protective measures, and infrastructure repair.'],
            ['Individual Assistance:', 'Coordinates support to households and survivors when authorized.'],
            ['Transition to recovery:', 'From response to short-term and long-term recovery priorities; JFO and DRCs as needed.'],
          ].map(([k, v], i) => (
            <li key={`rec-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Mitigation & Risk Reduction',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Enhanced SHMP alignment:', 'CEMP ties to the State Hazard Mitigation Plan for risk assessment and strategy.'],
            ['Grants:', 'BRIC, FMA, and HMGP fund resilient infrastructure, flood projects, and post-disaster mitigation.'],
            ['Community actions:', 'Defensible space, hardening, elevating utilities, and flood-ready retrofits reduce future loss.'],
          ].map(([k, v], i) => (
            <li key={`mit-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Continuity & Legal Framework',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Continuity of Operations:', 'Agencies maintain COOP/COG to ensure essential functions under all conditions.'],
            ['Authorities:', 'Actions guided by state statutes, executive orders, and federal regulations (e.g., Stafford Act, 44 CFR).'],
          ].map(([k, v], i) => (
            <li key={`cof-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Emergency Response and Recovery Operations" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Emergency Response and Recovery Operations</h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                We coordinate statewide preparedness, response, and recovery through a unified ICS/ESF structure, scalable SEOC
                activation, interoperable communications, and strong mutual-aid partnerships—so communities get rapid assistance,
                clear guidance, and sustained support from impact through long-term recovery.
              </p>

              {/* Download button BELOW overview, ABOVE other sections */}
              <div style={{ margin: '0 0 16px' }}>
                <a 
                  href="/guides/StateofGTS2025-cemp.pdf" 
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#e75934',
                    textDecoration: 'none',
                    border: '1px solid #e75934',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e75934';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#e75934';
                  }}
                >
                  <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                  Download Comprehensive Emergency Management Plan (PDF)
                </a>
              </div>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We deliver fast, coordinated response and resilient recovery—prioritizing life safety, equitable access,
                transparent communication, and mitigation that lowers future risk.
              </p>
            </div>

            <div style={{ marginTop: 12 }}>
              <Link to="/department/emergency">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default EmergencyResponseRecovery;
